import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Plus, Truck, Trash2, Loader2 } from "lucide-react";

const TIPOS = ["Proveedor tecnológico", "Consultora", "Universidad/Centro I+D", "Fabricante", "Otro"];
const RIESGOS = ["bajo", "medio", "alto"];
const ESTADOS = ["Activo", "En negociación", "Descartado"];
const RIESGO_COLOR = { bajo: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300", medio: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", alto: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" };

const EMPTY = { name: "", tipo: TIPOS[0], contacto: "", evaluacion: "", riesgo: "medio", estado: "Activo", notas: "" };

export function InitiativeSuppliersTab({ initiativeId, token }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    try {
      const data = await api.getSuppliers(token, initiativeId);
      setSuppliers(data);
    } catch {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  }, [token, initiativeId]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const handleCreate = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      await api.createSupplier(token, initiativeId, {
        ...form,
        evaluacion: form.evaluacion ? parseInt(form.evaluacion) : null,
      });
      toast.success("Proveedor añadido");
      setDialogOpen(false);
      setForm(EMPTY);
      fetchSuppliers();
    } catch {
      toast.error("Error al añadir proveedor");
    } finally {
      setSaving(false);
    }
  };

  const handleEstadoChange = async (id, estado) => {
    try {
      await api.updateSupplier(token, id, { estado });
      setSuppliers((prev) => prev.map((s) => (s.id === id ? { ...s, estado } : s)));
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteSupplier(token, id);
      toast.success("Proveedor eliminado");
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
    } catch {
      toast.error("Error al eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="suppliers-tab-content">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Análisis de Proveedores</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-2" />Añadir Proveedor</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo Proveedor</DialogTitle>
              <DialogDescription>Evalúa un proveedor, consultora o partner vinculado a esta iniciativa</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre del proveedor" data-testid="supplier-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="supplier-tipo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Riesgo</Label>
                  <Select value={form.riesgo} onValueChange={(v) => setForm({ ...form, riesgo: v })}>
                    <SelectTrigger data-testid="supplier-riesgo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{RIESGOS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contacto</Label>
                  <Input value={form.contacto} onChange={(e) => setForm({ ...form, contacto: e.target.value })} placeholder="Email o persona de contacto" />
                </div>
                <div className="space-y-2">
                  <Label>Evaluación (1-5)</Label>
                  <Select value={String(form.evaluacion || "")} onValueChange={(v) => setForm({ ...form, evaluacion: v })}>
                    <SelectTrigger data-testid="supplier-evaluacion-select"><SelectValue placeholder="Sin evaluar" /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={2} placeholder="Observaciones sobre el proveedor..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={!form.name || saving} data-testid="save-supplier-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {suppliers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay proveedores registrados para esta iniciativa</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {suppliers.map((s) => (
            <Card key={s.id} data-testid={`supplier-card-${s.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.tipo}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => setDeleteTarget(s)} data-testid={`delete-supplier-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-xs border-0 ${RIESGO_COLOR[s.riesgo] || ""}`}>Riesgo {s.riesgo}</Badge>
                  {s.evaluacion && <Badge variant="outline" className="text-xs">Eval. {s.evaluacion}/5</Badge>}
                  <Select value={s.estado} onValueChange={(v) => handleEstadoChange(s.id, v)}>
                    <SelectTrigger className="h-6 text-xs w-auto px-2" data-testid={`supplier-estado-${s.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {s.contacto && <p className="text-xs text-muted-foreground">Contacto: {s.contacto}</p>}
                {s.notas && <p className="text-sm">{s.notas}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{deleteTarget?.name}" de esta iniciativa. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-supplier">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget.id)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-supplier">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
